import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { REMARK_PLUGINS } from "@/lib/markdown";
import { X, Eye, Edit3, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, VENDOR_LABELS } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import LookPicker from "@/components/admin/LookPicker";

/**
 * Modal for editing an existing product's full editorial data.
 * Mounted as a sibling of ProductsPanel; null when not editing.
 */
export default function ProductEditModal({ slug, onClose, onSaved, categories }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setForm({
          title: r.data.title || "",
          description: r.data.description || "",
          image_url: r.data.image_url || "",
          price: String(r.data.price ?? ""),
          currency: r.data.currency || "USD",
          vendor: r.data.vendor || "amazon",
          affiliate_url: r.data.affiliate_url || "",
          category_slugs: r.data.category_slugs || [],
          tags: r.data.tags || [],
          featured: !!r.data.featured,
          sort_order: r.data.sort_order || 0,
          look_slugs: r.data.look_slugs || [],
        });
      })
      .catch(() => {
        toast.error("Could not load product");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [slug, onClose]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleCategory = (cslug) =>
    setForm((f) => ({
      ...f,
      category_slugs: f.category_slugs.includes(cslug)
        ? f.category_slugs.filter((x) => x !== cslug)
        : [...f.category_slugs, cslug],
    }));

  const save = async () => {
    if (!form.title.trim() || !form.affiliate_url.trim()) {
      toast.error("Title and affiliate URL are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        sort_order: Number(form.sort_order) || 0,
      };
      const { data } = await api.put(`/products/${slug}`, payload);
      toast.success(`Saved "${data.title}"`);
      onSaved(data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
      onClick={() => !busy && onClose()}
      data-testid="product-edit-modal"
    >
      <div
        className="brut bg-white w-full max-w-4xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 p-4 border-b-2 border-black sticky top-0 bg-white z-10">
          <Edit3 className="w-4 h-4" strokeWidth={2.5} />
          <div className="font-display text-2xl tracking-tight truncate">
            {loading ? "Loading…" : `Edit · ${form?.title || slug}`}
          </div>
          <a
            href={`/product/${slug}`}
            target="_blank"
            rel="noreferrer"
            data-testid="product-edit-view-live"
            className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1 ml-auto"
          >
            <ExternalLink className="w-3 h-3" /> View live
          </a>
          <button
            type="button"
            onClick={() => setShowPreview((s) => !s)}
            disabled={loading}
            data-testid="product-edit-toggle-preview"
            className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1"
          >
            {showPreview ? <Edit3 className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showPreview ? "Edit" : "Preview"}
          </button>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            data-testid="product-edit-close"
            className="brut bg-white p-1.5 brut-press"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>

        {loading || !form ? (
          <div className="p-8 font-mono uppercase text-xs tracking-widest opacity-60">
            Loading...
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="grid md:grid-cols-12 gap-3">
              <div className="md:col-span-8">
                <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
                  Title *
                </label>
                <input
                  value={form.title}
                  onChange={(e) => update({ title: e.target.value })}
                  data-testid="product-edit-title"
                  className="w-full brut bg-white px-3 py-2 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
                  Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => update({ price: e.target.value })}
                  data-testid="product-edit-price"
                  className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono"
                />
              </div>
              <div className="md:col-span-2">
                <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
                  Vendor
                </label>
                <select
                  value={form.vendor}
                  onChange={(e) => update({ vendor: e.target.value })}
                  data-testid="product-edit-vendor"
                  className="w-full brut bg-white px-3 py-2 focus:outline-none text-sm"
                >
                  {["amazon", "ebay", "etsy", "other"].map((v) => (
                    <option key={v} value={v}>
                      {VENDOR_LABELS[v] || v}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
                Affiliate URL *
              </label>
              <input
                value={form.affiliate_url}
                onChange={(e) => update({ affiliate_url: e.target.value })}
                data-testid="product-edit-affiliate-url"
                className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono text-xs"
              />
            </div>

            <div>
              <ImageUploader
                label="Product image"
                value={form.image_url}
                onChange={(url) => update({ image_url: url })}
                testId="product-edit-image"
                aspect="aspect-square"
              />
            </div>

            <div>
              <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
                Description{" "}
                <span className="opacity-60 normal-case tracking-normal">
                  (Markdown — **bold**, *italic*, lists, [links](url), tables)
                </span>
              </label>
              {showPreview ? (
                <div
                  className="brut bg-white p-4 min-h-[300px]
                    [&_h1]:font-display [&_h1]:text-2xl [&_h1]:mt-3 [&_h1]:mb-1
                    [&_h2]:font-display [&_h2]:text-xl [&_h2]:mt-3 [&_h2]:mb-1
                    [&_h3]:font-display [&_h3]:text-lg [&_h3]:mt-2 [&_h3]:mb-1
                    [&_p]:my-2
                    [&_strong]:font-bold [&_em]:italic
                    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ul]:space-y-1
                    [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
                    [&_a]:underline [&_a]:pcs-text-pink
                    [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:font-mono [&_code]:text-sm
                    [&_table]:w-full [&_table]:border-2 [&_table]:border-black [&_table]:my-3
                    [&_th]:border-b-2 [&_th]:border-black [&_th]:bg-gray-100 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-mono [&_th]:uppercase [&_th]:text-xs
                    [&_td]:border-b [&_td]:border-black/20 [&_td]:px-2 [&_td]:py-1
                    [&_blockquote]:border-l-4 [&_blockquote]:border-black [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:opacity-80"
                  data-testid="product-edit-preview"
                >
                  <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
                    {form.description || "_(no description yet)_"}
                  </ReactMarkdown>
                </div>
              ) : (
                <textarea
                  value={form.description}
                  onChange={(e) => update({ description: e.target.value })}
                  rows={12}
                  placeholder={"## Why we love it\nGreat fit, durable.\n\n### Pros\n- Lightweight\n- Easy to clean\n\n| Size | Chest |\n|------|-------|\n| S    | 36\"   |"}
                  data-testid="product-edit-description"
                  className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono text-xs resize-y"
                />
              )}
            </div>

            <div>
              <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-2">
                Categories
              </label>
              <div className="flex flex-wrap gap-2" data-testid="product-edit-categories">
                {(categories || []).map((c) => {
                  const on = form.category_slugs.includes(c.slug);
                  return (
                    <button
                      type="button"
                      key={c.slug}
                      onClick={() => toggleCategory(c.slug)}
                      data-testid={`product-edit-cat-${c.slug}`}
                      className={`brut px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press ${
                        on ? "pcs-bg-pink text-white" : "bg-white"
                      }`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-6">
                <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
                  Tags (comma-separated)
                </label>
                <input
                  value={form.tags.join(", ")}
                  onChange={(e) =>
                    update({
                      tags: e.target.value
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean),
                    })
                  }
                  data-testid="product-edit-tags"
                  className="w-full brut bg-white px-3 py-2 focus:outline-none text-sm font-mono"
                />
              </div>
              <div className="md:col-span-3">
                <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
                  Sort order
                </label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => update({ sort_order: e.target.value })}
                  data-testid="product-edit-sort-order"
                  className="w-full brut bg-white px-3 py-2 focus:outline-none font-mono"
                />
              </div>
              <div className="md:col-span-3">
                <label className="inline-flex items-center gap-2 cursor-pointer py-2">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => update({ featured: e.target.checked })}
                    data-testid="product-edit-featured"
                    className="w-4 h-4 border-2 border-black"
                  />
                  <span className="font-mono uppercase text-[10px] tracking-widest">
                    Featured
                  </span>
                </label>
              </div>
            </div>

            <LookPicker value={form.look_slugs} onChange={(look_slugs) => update({ look_slugs })} selfSlug={slug} />

            <div className="flex items-center justify-end gap-2 pt-3 border-t-2 border-black sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={() => !busy && onClose()}
                disabled={busy}
                data-testid="product-edit-cancel"
                className="brut bg-white px-3 py-2 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                data-testid="product-edit-save"
                className="brut pcs-bg-pink text-white px-4 py-2 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50"
              >
                {busy ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
